import { GoogleIcon, MetaIcon } from "components"

/**
 * NoAdsConnectedSection Component
 * 
 * Componente reutilizable que muestra una alerta cuando el usuario no tiene
 * cuentas publicitarias conectadas, con botones para conectar Google Ads y Meta Ads.
 * 
 * @param {function} connectGoogleAds - Función para conectar Google Ads
 * @param {function} startMetaOAuth - Función para conectar Meta Ads
 * @param {string} activePlatform - Plataforma activa ('google', 'meta', 'all')
 * @param {object} activeGoogleAccount - Cuenta activa de Google Ads
 * @param {object} activeMetaAccount - Cuenta activa de Meta Ads
 * @param {string} context - Contexto de uso ('campaigns', 'analytics', 'objetivos' u 'optimizaciones')
 */
const NoAdsConnectedSection = ({ connectGoogleAds, startMetaOAuth, activePlatform, activeGoogleAccount, activeMetaAccount, context = 'campaigns' }) => {
  const hasGoogleConnected = Boolean(activeGoogleAccount)
  const hasMetaConnected = Boolean(activeMetaAccount)

  const shouldShowGoogleButton = (activePlatform === 'all' || activePlatform === 'google') && !hasGoogleConnected
  const shouldShowMetaButton = (activePlatform === 'all' || activePlatform === 'meta') && !hasMetaConnected

  const getTitle = () => {
    if (activePlatform === 'google') {
      return '¡No tenés Google Ads conectado!'
    } else if (activePlatform === 'meta') {
      return '¡No tenés Meta Ads conectado!'
    } else {
      return '¡No tenés cuentas publicitarias conectadas!'
    }
  }

  const getDescription = () => {
    const action = context === 'analytics' ? 'ver analytics' : context === 'objetivos' ? 'crear y gestionar objetivos' : context === 'optimizaciones' ? 'ver optimizaciones' : 'ver campañas'
    
    if (activePlatform === 'google') {
      return `Necesitás conectar Google Ads para poder ${action} de Google.`
    } else if (activePlatform === 'meta') {
      return `Necesitás conectar Meta Ads para poder ${action} de Meta.`
    } else {
      return `Necesitás conectar y activar al menos una cuenta publicitaria para poder ${action}.`
    }
  }

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 transition-all duration-500 ease-out text-center w-full my-8">
      <div className="relative z-10">
        <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{getTitle()}</h3>
        <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mb-3" />
        <p className="mb-6 text-white/70">{getDescription()}</p>
      </div>
      <div className="relative z-10 flex flex-col gap-3 sm:gap-4 max-w-md mx-auto">
        {shouldShowGoogleButton && (
          <button
            onClick={() => connectGoogleAds(false)}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 active:scale-[0.98]"
          >
            <div className="flex items-center justify-center gap-2">
              <GoogleIcon width={24} height={24} />
              Conectar Google Ads
            </div>
          </button>
        )}
        {shouldShowMetaButton && (
          <button
            onClick={() => startMetaOAuth(false)}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 active:scale-[0.98]"
          >
            <div className="flex items-center justify-center gap-2">
              <MetaIcon width={24} height={24} />
              Conectar Meta Ads
            </div>
          </button>
        )}
      </div>
    </div>
  )
}

export default NoAdsConnectedSection 